import { ethers } from "ethers";
import isEqual from "lodash.isequal";
import {
  GetOrders200Response,
  OrderStatusResponse,
} from "../../codegen-api";
import { getCurrentChainConfig } from "../../config/chains";
import { parseSocketPayload } from "../../hooks/wss/parseFrame";
import { toMillis } from "../../utils/timestampUnits";
import { RealtimeOrderPayload } from "./types";

export const orderType = [
  { name: "maker", type: "address" },
  { name: "isBuy", type: "bool" },
  { name: "limitPrice", type: "uint256" },
  { name: "amount", type: "uint256" },
  { name: "salt", type: "uint256" },
  { name: "instrument", type: "uint256" },
  { name: "timestamp", type: "uint256" },
];

const processedRealtimeEvents = new Set<string>();
const recentRealtimeOpenOrders = new Map<
  string,
  { order: GetOrders200Response; seenAt: number }
>();
const RECENT_REALTIME_ORDER_TTL_MS = 30_000;

export const rememberRealtimeEvent = (eventId: string) => {
  if (processedRealtimeEvents.has(eventId)) {
    return false;
  }
  processedRealtimeEvents.add(eventId);
  if (processedRealtimeEvents.size > 200) {
    const oldest = processedRealtimeEvents.values().next().value;
    if (oldest) processedRealtimeEvents.delete(oldest);
  }
  return true;
};

// Timestamps arrive in s, ms (realtime rows built from Date.now()) and ns
// (REST). Comparing them raw sorted millisecond rows as ancient (FE-16).
const orderTimeMs = (order: GetOrders200Response) =>
  toMillis(order.created_timestamp || order.timestamp) ?? 0;

export const sortOrders = (orders: GetOrders200Response[]) =>
  orders.sort((a, b) => orderTimeMs(b) - orderTimeMs(a));

const realtimeOrderCacheKey = (accountKey: string, orderId: string) =>
  `${accountKey}:${orderId}`;

const isOpenOrderStatus = (status?: OrderStatusResponse) =>
  status === OrderStatusResponse.Opened ||
  status === OrderStatusResponse.Partial;

const pruneRecentRealtimeOrders = () => {
  const now = Date.now();
  recentRealtimeOpenOrders.forEach((value, key) => {
    if (now - value.seenAt > RECENT_REALTIME_ORDER_TTL_MS) {
      recentRealtimeOpenOrders.delete(key);
    }
  });
};

export const rememberRealtimeOpenOrder = (
  accountKey: string | undefined,
  order: GetOrders200Response
) => {
  if (!accountKey || !order.order_id || !isOpenOrderStatus(order.order_status)) {
    return;
  }
  pruneRecentRealtimeOrders();
  recentRealtimeOpenOrders.set(realtimeOrderCacheKey(accountKey, order.order_id), {
    order,
    seenAt: Date.now(),
  });
};

export const forgetRealtimeOpenOrder = (
  accountKey: string | undefined,
  orderId: string
) => {
  if (!accountKey) return;
  recentRealtimeOpenOrders.delete(realtimeOrderCacheKey(accountKey, orderId));
};

export const mergeRecentRealtimeOrders = (
  accountKey: string | undefined,
  orders: GetOrders200Response[]
) => {
  if (!accountKey) return sortOrders(orders);
  pruneRecentRealtimeOrders();

  const merged = new Map<string, GetOrders200Response>();
  orders.forEach((order) => {
    if (order.order_id) {
      merged.set(order.order_id, order);
    }
  });

  recentRealtimeOpenOrders.forEach(({ order }, key) => {
    if (!key.startsWith(`${accountKey}:`) || !order.order_id) {
      return;
    }
    if (!merged.has(order.order_id)) {
      merged.set(order.order_id, order);
    }
  });

  return sortOrders(Array.from(merged.values()));
};

/**
 * The orders list an `orders` snapshot should produce, or `undefined` when
 * writing it would change nothing (FE-8).
 *
 * The backend pushes the full open-orders snapshot every few seconds; writing
 * it unconditionally re-rendered every consumer each time and dropped a
 * just-placed order the snapshot did not list yet. Rows seen open on the
 * realtime channel in the last 30 s are kept (the same optimistic rows the
 * REST fetch keeps), and a snapshot structurally equal to the cache is skipped.
 */
export const reconcileOrdersSnapshot = (
  accountKey: string | undefined,
  current: GetOrders200Response[] | undefined,
  snapshotOrders: GetOrders200Response[]
): GetOrders200Response[] | undefined => {
  const next = mergeRecentRealtimeOrders(accountKey, [...snapshotOrders]);
  if (current && isEqual(sortOrders([...current]), next)) return undefined;
  return next;
};

/** Decodes a realtime frame (already parsed by the transport, or a JSON string); never throws. */
export const parseRealtimeMessage = (
  messageData: any
): RealtimeOrderPayload | undefined =>
  parseSocketPayload(messageData, { reportInvalid: false }) as
    | RealtimeOrderPayload
    | undefined;

export const formatRawUnits = (
  value: string | number | undefined,
  decimals: number
) => {
  try {
    return ethers.utils.formatUnits(String(value || "0").split(".")[0], decimals);
  } catch {
    return "0";
  }
};

// ── Order status (FE-6) ─────────────────────────────────────────────────────

/** Every order status the realtime feed is known to send, upper-cased. */
export const REALTIME_ORDER_STATUSES = [
  "OPEN",
  "OPENED",
  "PARTIALLY_FILLED",
  "PARTIAL",
  "FILLED",
  "CANCELLED",
  "CANCELED",
  "EXPIRED",
  "REJECTED",
  "FAILED",
] as const;

export type RealtimeOrderStatus = (typeof REALTIME_ORDER_STATUSES)[number];

const isRealtimeOrderStatus = (status: string): status is RealtimeOrderStatus =>
  (REALTIME_ORDER_STATUSES as readonly string[]).includes(status);

const assertNever = (value: never): never => {
  throw new Error(`Unhandled realtime order status: ${String(value)}`);
};

export type RealtimeStatusDisposition =
  /** The order rests on the book: insert or update its row. */
  | { kind: "open"; status: OrderStatusResponse }
  /** The order is finished: remove its row. */
  | { kind: "remove"; status: OrderStatusResponse }
  /** A status we do not know (or none): revalidate, never guess. */
  | { kind: "unknown" };

/**
 * What a realtime order status means for the open-orders list.
 *
 * The old mapping sent every status it did not list to "Opened", so EXPIRED,
 * REJECTED and FAILED orders were inserted as ghost open orders and re-injected
 * into REST results for 30 s. The switch is exhaustive over
 * REALTIME_ORDER_STATUSES: adding a status there without handling it here is a
 * type error.
 */
export const classifyRealtimeStatus = (
  status?: string
): RealtimeStatusDisposition => {
  const normalized = String(status ?? "").trim().toUpperCase();
  if (!isRealtimeOrderStatus(normalized)) return { kind: "unknown" };

  switch (normalized) {
    case "OPEN":
    case "OPENED":
      return { kind: "open", status: OrderStatusResponse.Opened };
    case "PARTIALLY_FILLED":
    case "PARTIAL":
      return { kind: "open", status: OrderStatusResponse.Partial };
    case "FILLED":
      return { kind: "remove", status: OrderStatusResponse.Filled };
    case "CANCELLED":
    case "CANCELED":
      return { kind: "remove", status: OrderStatusResponse.Cancelled };
    case "EXPIRED":
      return { kind: "remove", status: OrderStatusResponse.Expired };
    case "REJECTED":
    case "FAILED":
      return { kind: "remove", status: OrderStatusResponse.Rejected };
    default:
      return assertNever(normalized);
  }
};

/** The list status for a realtime status, or `undefined` when it is not one we know. */
export const mapRealtimeStatus = (
  status?: string
): OrderStatusResponse | undefined => {
  const disposition = classifyRealtimeStatus(status);
  return disposition.kind === "unknown" ? undefined : disposition.status;
};

export const getConfiguredPerpMarketName = (poolId: string) => {
  const markets = getCurrentChainConfig().contracts.perpetualDex?.markets || {};
  const normalizedPoolId = poolId.toLowerCase();
  return Object.entries(markets).find(
    ([, address]) => address.toLowerCase() === normalizedPoolId
  )?.[0];
};
